import { Box, Button, Divider, Menu, MenuItem, CircularProgress, Typography } from "@mui/material";
import type { MouseEvent } from "react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import { DropdownItem } from "./dropdown_item";
import { useGetApiNotificationsQuery } from "api/generatedApi";
import { usePostApiNotificationsMarkAsReadMutation } from "api/generatedApi";
import { Category } from "@mui/icons-material";
import { api } from "api/enhancedEndpoints";

export const NotificationDropdown = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [markAsRead] = usePostApiNotificationsMarkAsReadMutation();

  const { data: notificationsData, isLoading, error } = useGetApiNotificationsQuery();

  useEffect(() => {
    const interval = setInterval(() => {
      api.util.invalidateTags(["AllNotifications", "UnreadNotifications"])
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const notifications =
    notificationsData?.notifications?.map((n) => ({
      ProjectId: n.projectId,
      ProjectName: n.projectName,
      subject: n.subject,
      Category: n.category,
      notificationCount: n.unreadNotificationCount,
      date: n.latestNotificationReceivedAt,
    })) ?? [];

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Determine if there are any unread notifications
  let bNotification = notifications.some((n) => n.notificationCount ?? 0 > 0);

  const handleClick = async (notificationIndex: number) => {
    await markAsRead({ markNotificationGroupAsReadRequest: notifications[notificationIndex] });
    navigate(`/projects/${notifications[notificationIndex].ProjectId}/dashboard/extraworkagreements`);

    setAnchorEl(null);
  };

  function closeMenu(): void {
    setAnchorEl(null);
  }

  function openMenu(event: MouseEvent<HTMLButtonElement>): void {
    setAnchorEl(event.currentTarget);
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" sx={{ minWidth: { xs: "30px", sm: "50px", md: "70px" }, marginTop: "3px" }}>
      <Button data-testid="project-selector-open-menu-btn" variant="text" onClick={openMenu} sx={{ color: "#FFFFFF", display: "flex", alignContent: "center", justifyContent: "center", minWidth: 0 }}>
        {!bNotification ? <NotificationsIcon fontSize="medium" sx={{ marginBottom: 0.5 }} /> : <NotificationImportantIcon fontSize="medium" sx={{ marginBottom: 0.5, color: "secondary.main" }} />}
      </Button>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "primary.dark",
          },
          "& .MuiMenuItem-root:hover": {
            backgroundColor: "primary.main",
            color: "grey.900",
          },
        }}
      >
        <MenuItem aria-readonly sx={{ fontWeight: "bold", color: "white", pointerEvents: "none" }}>
          {t("account.notifications.title")}
        </MenuItem>
        <Divider color="white" sx={{ ml: 1, mr: 1 }} />
        {isLoading && (
          <MenuItem>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            <Typography color="white">{t("account.notifications.loading")}</Typography>
          </MenuItem>
        )}
        {error && (
          <MenuItem>
            <Typography color="error">{t("account.notifications.error")}</Typography>
          </MenuItem>
        )}

        {notifications.length === 0 && !isLoading && !error && (
          <MenuItem>
            <Typography color="white">{t("account.notifications.noNotifications")}</Typography>
          </MenuItem>
        )}
        {notifications.map((notification, index) => (
          <DropdownItem
            key={notification.ProjectId}
            title={notification.Category ?? ""}
            text={notification.ProjectName ?? ""}
            notificationCount={notification.notificationCount ?? 0}
            date={notification.date ?? ""}
            onClick={() => {
              handleClick(index);
              closeMenu();
            }}
          />
        ))}
      </Menu>
    </Box>
  );
};
